import React, { useCallback, useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';

Chart.register(...registerables);

interface LandfillEmissionsStackedChartProps {
  location: string;
  embedOnly?: boolean;
  onEmissionsCalculated?: (emissions: number | null) => void;
}

// Stacked area chart of ghg_emissions grouped by landfill variable
const LandfillEmissionsStackedChart: React.FC<LandfillEmissionsStackedChartProps> = ({ location, embedOnly = false, onEmissionsCalculated }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const creatingRef = useRef<boolean>(false);
  const [hasData, setHasData] = React.useState<boolean | null>(null);
  const [chartHeight, setChartHeight] = React.useState<number>(500);

  const buildChart = useCallback(async () => {
    if (!canvasRef.current) return;
    if (creatingRef.current) return;
    creatingRef.current = true;

    try {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      const existing = Chart.getChart(canvasRef.current as unknown as HTMLCanvasElement);
      if (existing) existing.destroy();
    } catch {}

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    await supabaseLocationService.initialize();
    const geofips = await supabaseLocationService.getGeofips(location);
    
    if (!geofips) {
      creatingRef.current = false;
      return;
    }

    // Get location info to determine if it's a city or county
    // Facility stacks are county-scope EPA GHGRP rows (not city inventory totals).
    const { data: locationData } = await supabaseClient
      .from('population')
      .select('geography_type, county')
      .eq('geofips', geofips)
      .limit(1);

    let queryGeofips = geofips;
    let scopeLabel = location;
    
    // If it's a city, try to get county geofips for landfill data
    if (locationData && locationData.length > 0 && locationData[0].geography_type === 'City') {
      const countyName = locationData[0].county;
      if (countyName) {
        scopeLabel = `${countyName} County facilities (context for ${location})`;
        const { data: countyData } = await supabaseClient
          .from('population')
          .select('geofips')
          .eq('location_name', countyName)
          .eq('geography_type', 'County')
          .limit(1);
        
        if (countyData && countyData.length > 0) {
          queryGeofips = countyData[0].geofips;
        }
      }
    }
    // scopeLabel set above for chart title

    // Fetch data from landfill_emissions_full grouped by landfill
    const { data, error } = await supabaseClient
      .from('landfill_emissions_full')
      .select('year, landfill, ghg_emissions')
      .eq('geofips', queryGeofips)
      .order('year', { ascending: true })
      .order('landfill', { ascending: true });

    if (error) {
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    // If no data, return early (hasData check will show message)
    if (!data || data.length === 0) {
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    setHasData(true);

    // Group data by landfill and year
    const landfillData: Record<string, Record<number, number>> = {};
    const yearsSet = new Set<number>();
    
    (data || []).forEach((r: any) => {
      const landfill = r.landfill || 'Unknown';
      const year = r.year;
      const emissions = Number(r.ghg_emissions || 0);
      
      yearsSet.add(year);
      
      if (!landfillData[landfill]) {
        landfillData[landfill] = {};
      }
      
      // Sum emissions if multiple rows for same landfill/year
      landfillData[landfill][year] = (landfillData[landfill][year] || 0) + emissions;
    });

    const years = Array.from(yearsSet).sort((a, b) => a - b);
    
    // Calculate 2023 total emissions for smart title
    if (onEmissionsCalculated) {
      const emissions2023 = years.includes(2023) 
        ? Object.values(landfillData).reduce((sum, landfill) => sum + (landfill[2023] || 0), 0)
        : null;
      onEmissionsCalculated(emissions2023);
    }
    
    // Filter landfills to only include those with actual data (sum > 0)
    const landfillsWithData = Object.keys(landfillData).filter(landfill => {
      const totalEmissions = years.reduce((sum, year) => sum + (landfillData[landfill][year] || 0), 0);
      return totalEmissions > 0;
    }).sort();

    // Calculate chart height based on number of landfills (for legend space at bottom)
    // Base height: 500px, add ~25px per landfill (accounts for legend wrapping)
    // When embedOnly, chart will use 100% height to fill container
    const numLandfills = landfillsWithData.length;
    if (!embedOnly) {
      const calculatedHeight = Math.max(500, 450 + (numLandfills * 25));
      setChartHeight(Math.min(calculatedHeight, 900)); // Cap at 900px to avoid excessive height
    }

    // Generate colors for each landfill
    const colors = [
      '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16',
      '#f97316', '#6366f1', '#14b8a6', '#a855f7', '#e11d48', '#0ea5e9', '#64748b'
    ];

    const datasets = landfillsWithData.map((landfill, index) => {
      const color = colors[index % colors.length];
      const data = years.map(year => landfillData[landfill][year] || 0);
      
      return {
        label: embedOnly ? '' : landfill, // Hide landfill names when embedOnly
        data: data,
        borderColor: color,
        backgroundColor: color, // Will be replaced with RGBA in datasetsWithRGBA
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        stack: 'stack1',
        _landfillIndex: index, // Store original index for color lookup
      };
    });

    // Convert hex to rgba for backgroundColor
    const datasetsWithRGBA = datasets.map((ds, index) => {
      // Use stored index or fallback to finding by label
      const colorIndex = (ds as any)._landfillIndex !== undefined 
        ? (ds as any)._landfillIndex 
        : landfillsWithData.indexOf(ds.label || '');
      const hex = colors[colorIndex % colors.length];
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      
      return {
        ...ds,
        backgroundColor: `rgba(${r}, ${g}, ${b}, 0.8)`,
        borderColor: hex,
      };
    });

    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: years,
        datasets: datasetsWithRGBA,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            bottom: 10,
            top: 5,
            left: 5,
            right: 5
          }
        },
        plugins: {
          legend: { 
            display: false,
            position: 'bottom',
            labels: {
              boxWidth: 12,
              padding: 6,
              font: {
                size: 10
              }
            }
          },
          title: {
            display: !embedOnly,
            text: `County landfill facility GHG (not inventory) — ${scopeLabel}`,
            font: { size: 14, weight: 'bold' },
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: function(context: any) {
                if (embedOnly) {
                  // Don't show landfill names in tooltip when embedOnly
                  const value = context.parsed.y || 0;
                  return `${value.toLocaleString(undefined, { maximumFractionDigits: 0 })} tons CO₂e`;
                }
                const label = context.dataset.label || '';
                const value = context.parsed.y || 0;
                return `${label}: ${value.toLocaleString(undefined, { maximumFractionDigits: 0 })} tons CO₂e`;
              }
            }
          }
        },
        scales: {
          x: {
            stacked: true,
            title: { display: true, text: 'Year' },
            ticks: { font: { size: 10 } }
          },
          y: {
            stacked: true,
            title: { display: true, text: 'GHG Emissions (tons CO₂e)' },
            beginAtZero: true,
          },
        },
        interaction: { mode: 'index', intersect: false },
      },
    });
    creatingRef.current = false;
  }, [location, embedOnly, onEmissionsCalculated]);

  useEffect(() => {
    buildChart();
    const canvasNode = canvasRef.current;
    return () => {
      try {
        if (chartRef.current) chartRef.current.destroy();
        if (canvasNode) {
          const existing = Chart.getChart(canvasNode as unknown as HTMLCanvasElement);
          if (existing) existing.destroy();
        }
      } catch {}
    };
  }, [buildChart]);

  if (hasData === false) {
    return (
      <div className="chart-container" style={{ padding: '40px', textAlign: 'center' }}>
        <p>No landfill emissions data available for {location}.</p>
      </div>
    );
  }

  // When embedOnly, chart fills container completely (100% height)
  // When not embedOnly, use calculated height based on number of landfills
  const embedChartHeight = embedOnly ? '100%' : (typeof chartHeight === 'number' ? `${chartHeight}px` : chartHeight);
  
  return (
    <div className="chart-container" style={{ height: embedOnly ? '100%' : (typeof chartHeight === 'number' ? `${chartHeight}px` : chartHeight), width: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
      <canvas ref={canvasRef} style={{ height: embedChartHeight, width: '100%' }} />
    </div>
  );
};

export default LandfillEmissionsStackedChart;

